import { useMemo, useState } from 'react'
import {
  Alert,
  Badge,
  Card,
  Center,
  Container,
  Grid,
  Group,
  Loader,
  Progress,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { IconAlertTriangle, IconClock, IconTargetArrow, IconLock } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { fetchDepartmentReport, fetchManagerDashboard } from '../shared/api/kpi'
import { fetchDepartments } from '../shared/api/departments'
import { useAuthStore } from '../features/auth/store'
import { AppLayout } from '../features/projects/app-shell'
import { isApiError } from '../shared/api/client'
import { formatDuration, onTimeRate } from '../features/reports/format'
import { MetricBlock, SubStat, EmptyData } from '../features/reports/metric-block'

function currentMonthRange(): { from: string; to: string } {
  const now = new Date()
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0))
  return { from: from.toISOString(), to: now.toISOString() }
}

export function ReportsDashboardPage() {
  const { user } = useAuthStore()
  const isManagerView = user?.role === 'admin' || user?.role === 'director'
  const period = useMemo(currentMonthRange, [])

  // head → строго свой отдел; admin/director → селектор
  const deptsQ = useQuery({ queryKey: ['departments'], queryFn: fetchDepartments, enabled: isManagerView })
  const [selectedDept, setSelectedDept] = useState<string | null>(null)
  const targetDept = isManagerView ? selectedDept ?? deptsQ.data?.[0]?.id ?? null : user?.departments?.[0]?.id ?? null

  return (
    <AppLayout>
      <Container size="xl" py="md">
        <Group justify="space-between" mb="lg">
          <div>
            <Text size="xs" c="blue.7" fw={700} tt="uppercase">
              Дашборд руководителя
            </Text>
            <Title order={2}>{user?.departments?.[0]?.name ?? 'Отдел'}</Title>
          </div>
          {isManagerView && deptsQ.data && (
            <Select
              label="Отдел"
              data={deptsQ.data.map((d) => ({ value: d.id, label: d.name }))}
              value={targetDept}
              onChange={setSelectedDept}
              w={220}
            />
          )}
        </Group>

        {targetDept ? (
          <DashboardBody departmentId={targetDept} from={period.from} to={period.to} />
        ) : (
          <Center mih={200}>
            <Loader />
          </Center>
        )}
      </Container>
    </AppLayout>
  )
}

function DashboardBody({ departmentId, from, to }: { departmentId: string; from: string; to: string }) {
  const reportQ = useQuery({
    queryKey: ['dept-report', departmentId, from, to],
    queryFn: () => fetchDepartmentReport(departmentId, from, to),
    retry: false,
  })
  const dashQ = useQuery({
    queryKey: ['manager-dashboard', departmentId, from, to],
    queryFn: () => fetchManagerDashboard(departmentId, from, to),
    retry: false,
  })

  // 403 — внятный отказ, не белый экран
  const forbidden =
    (isApiError(reportQ.error) && reportQ.error.response?.status === 403) ||
    (isApiError(dashQ.error) && dashQ.error.response?.status === 403)
  if (forbidden) {
    return (
      <Alert color="red" icon={<IconLock size={18} />} variant="light" title="Нет доступа">
        У вас нет прав на просмотр дашборда этого отдела. Руководитель видит только свой отдел.
      </Alert>
    )
  }

  if (reportQ.isLoading || dashQ.isLoading) {
    return (
      <Center mih={300}>
        <Loader />
      </Center>
    )
  }
  if (reportQ.error || dashQ.error || !reportQ.data || !dashQ.data) {
    return <Text c="red">Не удалось загрузить данные. Обновите страницу.</Text>
  }

  const { duration, on_time } = reportQ.data.totals
  const emptyDuration = duration.sample_size === 0

  return (
    <Stack gap="lg">
      {/* ДВА РАВНОПРАВНЫХ БЛОКА — одинаковый вес, симметрия, без «главной цифры» */}
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <MetricBlock
            icon={<IconClock size={20} />}
            title="Длительность выполнения"
            color="blue"
          >
            {emptyDuration ? (
              <EmptyData />
            ) : (
              <Group grow gap="xl">
                <SubStat label="Среднее" value={formatDuration(duration.avg_seconds)} />
                <SubStat label="Медиана" value={formatDuration(duration.median_seconds)} />
              </Group>
            )}
          </MetricBlock>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <MetricBlock
            icon={<IconTargetArrow size={20} />}
            title="Соблюдение срока"
            color="teal"
          >
            {on_time.on_time + on_time.overdue + on_time.no_due === 0 ? (
              <EmptyData />
            ) : (
              <Group grow gap="xl">
                <SubStat label="В срок" value={String(on_time.on_time)} sub={onTimeRate(on_time.on_time, on_time.overdue)} />
                <SubStat label="Просрочено" value={String(on_time.overdue)} danger={on_time.overdue > 0} />
                <SubStat label="Без срока" value={String(on_time.no_due)} muted />
              </Group>
            )}
          </MetricBlock>
        </Grid.Col>
      </Grid>

      {/* Живые срезы */}
      <Grid>
        <LiveSlice label="Открыто задач" value={dashQ.data.open_total} />
        <LiveSlice label="Просрочено сейчас" value={dashQ.data.overdue_total} danger />
        <LiveSlice label="Без исполнителя" value={dashQ.data.unassigned_total} warn />
        <LiveSlice label="Выполнено за период" value={dashQ.data.completed_in_period} good />
      </Grid>

      {/* Рейтинг сотрудников по своевременности */}
      <Card withBorder p="md" radius="md">
        <Text fw={600} mb="sm">
          Сотрудники отдела · своевременность
        </Text>
        {reportQ.data.employees.length === 0 ? (
          <EmptyData />
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Сотрудник</Table.Th>
                <Table.Th>Выполнено</Table.Th>
                <Table.Th>В срок</Table.Th>
                <Table.Th>Просрочено</Table.Th>
                <Table.Th>Доля в срок</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {reportQ.data.employees.map((e) => (
                <Table.Tr key={e.employee.id}>
                  <Table.Td>{e.employee.full_name}</Table.Td>
                  <Table.Td>{e.completed_count}</Table.Td>
                  <Table.Td>{e.on_time}</Table.Td>
                  <Table.Td c={e.overdue > 0 ? 'red.7' : undefined}>{e.overdue}</Table.Td>
                  <Table.Td style={{ minWidth: 120 }}>
                    {e.on_time_rate === null ? (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    ) : (
                      <Group gap="xs">
                        <Progress value={e.on_time_rate * 100} w={70} color={e.on_time_rate >= 0.9 ? 'green' : e.on_time_rate >= 0.7 ? 'yellow' : 'red'} />
                        <Text size="sm">{Math.round(e.on_time_rate * 100)}%</Text>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Stack>
  )
}

function LiveSlice({ label, value, danger, warn, good }: { label: string; value: number; danger?: boolean; warn?: boolean; good?: boolean }) {
  const color = danger ? 'red.7' : warn ? 'orange.7' : good ? 'green.7' : 'ink'
  return (
    <Grid.Col span={{ base: 6, md: 3 }}>
      <Card withBorder p="md" radius="md">
        <Group gap={6}>
          {danger && value > 0 && <IconAlertTriangle size={16} color="var(--mantine-color-red-7)" />}
          <Text size="28px" fw={800} c={color} style={{ fontFeatureSettings: '"tnum"' }}>
            {value}
          </Text>
        </Group>
        <Text size="xs" c="dimmed" mt={4}>
          {label}
        </Text>
      </Card>
    </Grid.Col>
  )
}
